import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import logger from '@gral/log';
import ReactDOMServer from 'react-dom/server';
import envJson from '../../../../.env.json';

//OPEN LAYERS
import OlMousePosition from 'ol/control/MousePosition';
import { Attribution, defaults as defaultControls } from 'ol/control.js';
import Feature, { FeatureLike } from 'ol/Feature';
import OlMap from 'ol/Map';
import OlImageTile from 'ol/ImageTile';
import OlLayerTile from 'ol/layer/Tile';
import OlPoint from 'ol/geom/Point';
import OlOverlay from 'ol/Overlay';
import OlLayerVector from 'ol/layer/Vector';
import OlLayerImage from 'ol/layer/Image';
import OlSourceImageStatic from 'ol/source/ImageStatic';
import OlSourceVector from 'ol/source/Vector';
import OlSourceOSM from 'ol/source/OSM';
import { Extent, getCenter, extend } from 'ol/extent';
import XYZ from 'ol/source/XYZ';
import { transform } from 'ol/proj';

//Utils
import { isExtentIntersects, generateOpenLayerMapView, computeExtent, updateMapElement } from '@components/Maps/utils/map.utils';

//Interfaces
import { CView, CElement, IGeometry, IView, CUnitFormatter, formatValue, EFlavors } from '@ts/ui-models';

import { Icon } from '@ts/storybook';
import { makeStyles } from '@mui/styles';
import { Theme, useTheme } from '@mui/material/styles';
import { IconButton } from '@mui/material';
import { Grid, Typography } from '@ts/storybook';
import Tooltip from '@components/Outputs/Tooltip';
import InfoSidebar from '@components/Maps/InfoSideBar';

//DATA
import { Loading } from '@components/Misc';
import _ from 'lodash';
import MapIndoorLayer from '@components/Maps/Map/MapIndoorLayer';

import { useAppDispatch, useAppSelector, useObjectSelector } from '@redux/store';
import { useSetState } from 'react-use';
import { WrapperEntitySelector } from '@components/Inputs';
import { requestActions } from '@redux/slices/functionals/request/requestSlice';
import { getSettings, putUserDefaultView } from '@services/ApiService';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { fetchView } from '@services/DataBase/Map.service';
import { Coordinate } from 'ol/coordinate';
import useEffectAfterMount from '@utils/hooks/useEffectAfterMount';
import { CMapElement } from '@models/maps/Map';
import { CMapLayer } from '@models/maps/Layer';
import { globalActions } from '@redux/slices/states/global/globalSlice';

const useStyles = makeStyles<Theme, { theme: Theme; isViewSatellite: boolean }>(() => ({
  quickmap: props => ({
    position: 'relative',
    display: 'flex',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    border: '1px solid ' + props.theme.palette.primary.main,
  }),
  map: {
    position: 'relative',
    height: 'calc(100% + 15px)',
    width: '100%',
    '& .ol-touch .ol-control button': {
      fontSize: '1.2rem',
    },
  },
  iconBtnMap: (props: any) => ({
    backgroundColor: 'rgba(0, 60, 136, 0.5)',
    color: '#fff',
    textAlign: 'center',
    border: props.isViewSatellite ? '2px solid rgba(255, 255, 255, 0.4)' : '2px solid rgba(255, 255, 255, 0.7)',
    borderRadius: '4px',
    padding: '2px',
    cursor: 'pointer',
    marginRight: '5px',
    height: '32px',
    width: '32px',
    display: 'flex',
  }),
}));

interface TMapProps {
  element?: CElement;
  isModal?: boolean; //not yet used
  isLoading?: boolean; //not yet used
  isQuickAccess?: boolean;
  view?: CView | null;
  isGeometryDrawer?: boolean;
  disabled?: boolean;
  mode?: 'view' | 'edit' | 'new';
  hideInfoSideBar?: boolean;
  onOlmapChange?: (map: OlMap, view: CView) => void; //Send ref OpenLayer map
  onCenterView?: () => void; //PolyFunction to centerView
}

interface StateMap {
  view?: CView | null;
  selectedViewId?: string; //When change the view in setting
  layers: { [key: string]: any };
  vectorSource: { [key: string]: any };
  features: { [key: string]: any[] };
  imageLayer: any;
}

const t3857 = (v: any) => v && transform(v, 'EPSG:4326', 'EPSG:3857');
const accessToken = envJson.APIKEY_MAP;

const log = logger(`QUICKMAP`);
const QuickMap = forwardRef((props: TMapProps, ref: any) => {
  let tooltip: any;
  let currentFeature: any = null;
  const theme = useTheme();
  const dispatch = useAppDispatch();
  // defaultViewId is posible to be undefined should not give error, using defaultView
  const settings = useAppSelector(state => state.global?.settings);
  const badge = useAppSelector(state => state.global?.sessionBadge);
  const readingTypeList = useAppSelector(state => state.global.readingTypesList);
  const unitsList = useAppSelector(state => state.global.unitsList);
  const unitFormatter = new CUnitFormatter(unitsList, readingTypeList);

  const defaultViewId = settings?.defaultView?.defaultViewId;

  const mapRef = useRef<HTMLDivElement>(null);
  const [olmap, setOlMap] = useState<OlMap | null>(null);

  const [tElement, setTElement] = useState<CElement | undefined | null>(props.element); //Only to get the data, use listItems
  const [tMapElement, setCMapElement] = useState<CMapElement | null>(null); //Only to get the data, use listItems

  const {
    data: defaultViewFetch,
    loading: defaultViewLoading,
    error: defaultViewError,
  } = useObjectSelector(state => state.request['mapView']);
  const [stateMap, setStateMap] = useSetState<StateMap>({
    view: null,
    selectedViewId: undefined,
    layers: {},
    vectorSource: {},
    features: {},
    imageLayer: null,
  });
  const prevStateMapView: any = useRef(null);

  const [state, setState] = useSetState<any>({
    isViewSatellite: settings?.defaultView?.tile === 'satellite' ? true : false, //Change styles and layer to satellite
    loading: true,
    hideOtherGeometries: false,
    is2D: false,
  });

  const defaultView: CView = CView.generateDefaultValues();

  //#region Handlers
  const onClickHideOtherGeometries = () => {
    setState((prevState: any) => ({ ...prevState, hideOtherGeometries: !prevState.hideOtherGeometries }));
    changeVisibilityLayer('layer_other_geometries', state.hideOtherGeometries);
  };

  const onChangeTileView = () => {
    const idOldLayer = state.isViewSatellite ? 'base_layer_satellite' : 'base_layer_osm';
    const idNewLayer = !state.isViewSatellite ? 'base_layer_satellite' : 'base_layer_osm';

    changeVisibilityLayer(idOldLayer, false);
    changeVisibilityLayer(idNewLayer, true);
    //replaceLayerById('base_layer', newLayers);
    setState((prevState: any) => ({ ...prevState, isViewSatellite: !prevState.isViewSatellite }));

    putSettingsUserTileDefaultView(!state.isViewSatellite ? 'satellite' : 'normal');
  };

  const onChangeView = (v: CView) => {
    const { parameterId } = settings?.defaultView || {};
    if (v?.getID() && stateMap.selectedViewId !== v.getID()) {
      setStateMap({ view: null, selectedViewId: v.getID() });
      putUserDefaultView(dispatch, parameterId, v.getID()).then(v => {
        if (v.payload.data?.value) {
          dispatch(
            globalActions.updateGlobalState({
              key: 'settings',
              value: {
                ...settings,
                defaultView: { ...settings?.defaultView, defaultViewId: v.payload.data.value.defaultViewId },
              },
            })
          );
          dispatch(requestActions.cleanKey('defaultView'));
        }
      });
    }
  };

  //#endregion

  //#region Functions
  const isLoading = () => {
    return (!props.isGeometryDrawer && !tMapElement) || defaultViewLoading || props.isLoading;
  };

  //#region Tooltip
  const generateTooltip = olmap => {
    tooltip = document.getElementById('tooltip');
    olmap.getTargetElement().addEventListener('pointerleave', () => {
      currentFeature = undefined;
      tooltip.style.visibility = 'hidden';
    });
  };
  /**
   * Updates the mouse position display element with a formatted value if available and applicable.
   *
   * We need this in items with position to update the coordinates tooltip
   */
  const defaultValueMousePosition = () => {
    const mousePositionElements = document.getElementsByClassName('ol-mouse-position');
    if (mousePositionElements.length > 0) {
      const mousePositionElement = mousePositionElements[0];

      if (tElement?.lastPosition && tElement.mainFlavor !== EFlavors.LOCATION) {
        const formattedValue = formatValue(
          tElement?.getLastPosition(stateMap.view?.getReadingTypePosition())?.data,
          null,
          stateMap.view?.getReadingTypePosition()
        );
        if (formattedValue !== 'undefined') mousePositionElement.innerHTML = formattedValue;
      }
    }
  };
  //#endregion

  //#region View
  const adjustZoomIndoorLayerEvent = () => {
    olmap?.on('pointermove', throttledAdjustZoomIndoorLayer);
  };

  useEffect(() => {
    // Agrega el evento cuando cambia `state.isViewSatellite`
    adjustZoomIndoorLayerEvent();

    return () => {
      // Elimina el evento cuando cambian las dependencias o el componente se desmonta
      olmap?.un('pointermove', throttledAdjustZoomIndoorLayer);
    };
  }, [adjustZoomIndoorLayerEvent, state.isViewSatellite]);

  const fetchDefaultView = async () => {
    //defaultViewFetch = undefined, try to found, defaultViewFetch = null use default value
    const viewId = stateMap.selectedViewId || defaultViewId;

    if (
      defaultViewFetch !== undefined ||
      (defaultViewFetch === undefined && defaultViewError) ||
      (!defaultViewFetch && !viewId)
    ) {
      let newView: CView | null = null;
      if (defaultViewFetch && !defaultViewFetch.errors) {
        const newViewLastPosition: IView = { ...defaultViewFetch };
        newViewLastPosition.properties = { ...defaultViewFetch.properties, mode: 'lastPosition', now: true };
        newView = new CView(newViewLastPosition);
        if (!newView.getID()) newView = defaultView;
      } else {
        newView = defaultView;
      }
      log.info('Set View', newView);
      setStateMap({ view: newView });
      setState({ is2D: newView.viewTypeId === '2d' });
    } else if (viewId && !defaultViewLoading) {
      log.info('FetchView', viewId);
      fetchView(dispatch, viewId);
    }
  };
  //#endregion

  //#region Data
  /**
   * Determines if there are features in the map.
   *
   * @return {boolean} Returns true if there are features in the map, otherwise false.
   */
  const someItemWithLocation = () => {
    let haveGPSLocation = false;

    if (stateMap.view) {
      if (tMapElement?.lastPosition) {
        haveGPSLocation = true;
      }
    }
    return haveGPSLocation;
  };

  const getCoordinatesFromItem = (item: CMapElement) => {
    if (stateMap.view) {
      const coordinateData: { x: number; y: number } = item.lastPosition?.data;
      if (coordinateData && typeof coordinateData === 'object') return [coordinateData.x, coordinateData.y];
    }
    return null;
  };
  //#endregion

  //#region Map
  const createControls = () => {
    const attribution = new Attribution({
      collapsible: false,
    });
    const mousePositionControl = new OlMousePosition({
      coordinateFormat: coordinate => {
        if (tElement?.lastPosition && tElement.mainFlavor !== EFlavors.LOCATION) {
          return formatValue(
            tElement?.getLastPosition(stateMap.view?.getReadingTypePosition())?.data,
            null,
            stateMap.view?.getReadingTypePosition()
          );
        } else return formatValue({ x: coordinate?.[0], y: coordinate?.[1] }, null, stateMap.view?.getReadingTypePosition());
      },
      projection: stateMap.view?.getProjectionCode(),
    });

    return [attribution, mousePositionControl];
  };

  const updateMap = () => {
    olmap?.render();
    requestAnimationFrame(updateMap);
  };

  const changeVisibilityLayer = (idLayer: string, visibility: boolean) => {
    const olLayer = olmap
      ?.getLayers()
      .getArray()
      .find(layer => layer.get('id') === idLayer);
    olLayer?.setVisible(visibility);

    setTimeout(() => {
      updateMap();
    }, 500);
  };

  const checkIfLayerCreated = logicalId => {
    const layers = olmap?.getLayers();
    const createdLayers: any[] = [];
    layers?.forEach(layer => {
      if (layer && layer.get('id') !== 'tileLayer') {
        createdLayers.push(layer);
      }
    });
    return createdLayers.find(layer => layer.get('id') === logicalId);
  };

  /**
   * This function generates a background image layer for the map based on the provided taLayer.
   * @param {*} taLayer
   */
  const generateMapLayerBackgroundImages = taLayer => {
    if (!taLayer) return;
    const { background } = taLayer;
    const backgroundLayerCreated = checkIfLayerCreated(taLayer.logicalId);
    if (background && !backgroundLayerCreated) {
      const imageExtent = computeExtent(taLayer.properties);

      if (imageExtent.length) {
        const locator = typeof background.locator === 'object' ? background.locator.data : background.locator;

        const backgroundImage = new OlSourceImageStatic({
          url: locator,
          projection: 'EPSG:3857',
          imageExtent,
        });

        const backgroundLayer = new OlLayerImage({
          source: backgroundImage,
        });
        backgroundLayer.set('id', 'imageLayer' + taLayer.logicalId);
        return backgroundLayer;
      }
    }
  };

  /**
   * Generates the image layer based on the xViewLayers of the view.
   *
   * @return {void}
   */
  const generateImageLayer = () => {
    stateMap.view?.xViewLayers?.forEach(xViewLayers => {
      const { background } = xViewLayers.layer || {};
      if (background) {
        const locator = background.locator instanceof Object ? background.locator.data : background.locator;
        setStateMap({
          imageLayer: new MapIndoorLayer(olmap, locator, new CMapLayer(xViewLayers), stateMap.view?.xViewLayers, 1),
        });
      }
    });
  };

  /**
   * Generates tile layers for the map.
   *
   * @return {Array<OlLayerTile>} An array of tile layers.
   */
  const generateTileLayers = () => {
    const proSource = source => {
      source.setTileLoadFunction((tile, src) => {
        const imageTile = tile as OlImageTile;
        const image = imageTile.getImage() as any;
        fetch(src)
          .then(response => {
            if (retryCodes.includes(response.status)) {
              retries[src] = (retries[src] || 0) + 1;
              if (retries[src] <= 3) {
                setTimeout(() => tile.load(), retries[src] * 1000);
              }
              return Promise.reject();
            }
            return response.blob();
          })
          .then(blob => {
            const imageUrl = URL.createObjectURL(blob);
            image.onload = () => {
              URL.revokeObjectURL(imageUrl);
            };
            image.onerror = () => {
              URL.revokeObjectURL(imageUrl);
            };
            // @ts-ignore
            image.src = imageUrl;
            setTimeout(() => URL.revokeObjectURL(imageUrl), 5000);
          })
          .catch(() => tile.setState(3));
      });
      return source;
    };

    const sourceOSM = proSource(new OlSourceOSM());

    const sourceSatellite = proSource(
      new XYZ({
        url: `https://ibasemaps-api.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}?token=${accessToken}`,
      })
    );

    const retryCodes = [408, 429, 500, 502, 503, 504];
    const retries = {};

    const osmLayer = new OlLayerTile({
      source: sourceOSM,
      zIndex: 0,
    });

    const satelliteLayer = new OlLayerTile({
      source: sourceSatellite,
      zIndex: 0,
    });

    return [osmLayer, satelliteLayer];
  };

  /**
   * Generates the icon of the last position of each item and updates it.
   *
   * @param {type} taLayer - the TA layer
   * @param {type} item - the item
   * @param {type} position - the position of the icon
   * @param {type} name - the name of the icon
   * @param {type} color - the color of the icon
   */
  const generateOpenLayersMap = (view: CView) => {
    prevStateMapView.current = stateMap.view;
    let layers: any[] = [];

    if (!stateMap.view?.isWorldView()) {
      //2d
      const { xViewLayers } = view;
      const viewLayers =
        xViewLayers &&
        xViewLayers
          .map(v => new CMapLayer(v))
          .map(xViewLayer => ({
            logicalId: xViewLayer.layer.getID(),
            background: xViewLayer.layer.background,
            properties: xViewLayer.properties,
          }));

      layers = viewLayers?.map(layer => generateMapLayerBackgroundImages(layer)) || [];
      layers = layers.filter(layer => layer !== undefined);
    } else {
      //GPS
      layers = generateTileLayers();
      layers[0].set('id', 'base_layer_osm');
      layers[0].setVisible(!state.isViewSatellite);
      layers[1].set('id', 'base_layer_satellite');
      layers[1].setVisible(state.isViewSatellite);
    }

    const { view: mapView } = generateOpenLayerMapView(view || defaultView);
    const mapElement = document.getElementById('map');

    if (mapElement) {
      const viewPort = mapElement.getElementsByClassName('ol-viewport')[0];
      if (viewPort) {
        mapElement.removeChild(viewPort);
      }
    }

    const newMap = new OlMap({
      layers,
      view: mapView,
      target: 'map',
      controls: defaultControls({ attribution: false }).extend(createControls()),
    });

    //Listeners
    newMap.on('pointermove', pointerMoveInMap(newMap));

    generateTooltip(newMap);
    log.info('Map mounted', stateMap.view, props);
    setOlMap(newMap);

    defaultValueMousePosition();
  };

  /**
   * Generates an array of features from the list of items.
   *
   * @return {Feature<OlPoint>[]} An array of features.
   */
  const generateFeaturesFromItems = (): Feature<OlPoint>[] => {
    if (!tMapElement) return [];
    log.info('GenerateFeaturesFromItems');
    return [tMapElement]
      .map(item => {
        const coordinates = getCoordinatesFromItem(item);
        if (coordinates) {
          const punto = new OlPoint(state.is2D ? coordinates : t3857(coordinates));
          return new Feature(punto);
        }
        return null;
      })
      .filter(item => item !== null) as Feature<OlPoint>[];
  };

  /**
   * Generates a vector source for the given taLayer.
   *
   * @param {any} taLayer - The taLayer object.
   * @return {void}
   */
  const generateVectorSource = (taLayer: { logicalId: string }) => {
    //ILayer in future
    const { logicalId: layerLogicalId } = taLayer as { logicalId: string };
    const copyStateMap = { ...stateMap };

    const featuresDone = generateFeaturesFromItems();

    copyStateMap.vectorSource[layerLogicalId] = new OlSourceVector({
      features: featuresDone,
    });
    copyStateMap.features[layerLogicalId] = featuresDone;
    setStateMap({ vectorSource: copyStateMap.vectorSource, features: copyStateMap.features });
  };

  /**
   * Generates a vector layer for the given taLayer.
   *
   * @param {any} taLayer - The taLayer object.
   */
  const generateVectorLayer = (taLayer: { logicalId: string }) => {
    //ILayer in future
    let vectorSource = stateMap.vectorSource[taLayer.logicalId];
    vectorSource[taLayer.logicalId] = new OlSourceVector();
    const vectorLayer = new OlLayerVector({
      source: vectorSource,
      zIndex: 2,
    });
    vectorLayer.set('id', 'id' + taLayer.logicalId);

    olmap?.addLayer(vectorLayer);
  };

  //#region View
  const centerViewOnFeatures = () => {
    log.info('CenterViewOnFeatures');
    const listFeatures: any[][] = Object.values(stateMap.features);
    const flattenedFeatures: any[] = listFeatures.flat();
    const olView = olmap?.getView();

    let combinedExtent: Extent | null = (flattenedFeatures[0] && flattenedFeatures[0].getGeometry()?.getExtent()) || null;

    flattenedFeatures.forEach(feature => {
      const featureExtent = feature.getGeometry()?.getExtent();
      if (featureExtent && combinedExtent) {
        combinedExtent = extend(combinedExtent, featureExtent);
      }
    });

    if (flattenedFeatures.length === 1) {
      const zoomLevel = 18;
      olView?.animate({
        center: getCenter(combinedExtent!),
        zoom: zoomLevel,
        duration: 0,
      });
    } else if (combinedExtent) {
      olView?.fit(combinedExtent, { padding: [10, 10, 10, 10], duration: 0 });
    }
    /*
      else {
       if (stateMap.imageLayer && state.isGPS) {
        const extentImageLayer = stateMap.imageLayer.getExtent;
        olView?.fit(extentImageLayer, { padding: [50, 50, 50, 50], duration: 1000 });
         }
      }
    */
    updateMap();
  };

  /**
   * A function that centers the view. If onCenterView is defined in the props, it will call that function.
   *
   * @return {void}
   */
  const onCenterView = () => {
    if (!tMapElement && props.onCenterView) {
      props.onCenterView();
      return;
    }
    centerViewOnFeatures();
  };

  /**
   * Generate icons based on the given parameters.
   *
   * @param {any} taLayer - The taLayer object.
   * @param {CElement} item - The item object.
   * @param {[number, number]} position - The position of the icon.
   * @param {string} name - The name of the icon.
   * @param {string} color - The color of the icon.
   * @param {boolean} showIcon - Whether to show the icon or not. Default is true.
   */
  const generateIcon = (itemID: string, position: Coordinate, icon) => {
    position = state.is2D ? position : t3857(position);

    const overlayIconExist = olmap?.getOverlayById(itemID);
    if (overlayIconExist) {
      overlayIconExist.setPosition(position);
      const oldElement = overlayIconExist.getElement();
      if (oldElement) oldElement.innerHTML = ReactDOMServer.renderToStaticMarkup(<Icon isMap icon={icon} />);
    } else {
      const iconElement = document.createElement('div');
      iconElement.id = itemID;
      iconElement.innerHTML = ReactDOMServer.renderToStaticMarkup(<Icon isMap icon={icon} />);
      iconElement.style.display = '';
      const overlay = new OlOverlay({
        id: itemID,
        element: iconElement,
        position: position,
        positioning: 'center-center',
        stopEvent: false,
      });
      olmap?.addOverlay(overlay);
    }
  };

  //Adjusts the zoom depending on whether we are in an indoor layer
  const adjustZoomIndoorLayer = () => {
    if (stateMap.imageLayer) {
      const extentImageLayer = stateMap.imageLayer.getExtent;
      const view = olmap?.getView();

      if (view) {
        const viewExtent = view.calculateExtent(olmap?.getSize());
        const newZoom = view.getZoom() || 0;

        if (viewExtent && extentImageLayer && isExtentIntersects(viewExtent, extentImageLayer)) {
          if (newZoom < 24 && newZoom > 2) {
            view.setMaxZoom(24);
          }
        } else {
          const maxZoomInSatellite = state.isViewSatellite ? 19 : 20;

          if (newZoom < maxZoomInSatellite && newZoom > 2) {
            view.setMaxZoom(maxZoomInSatellite);
          }
        }
      }
    }
  };

  const displayFeatureTooltip = (olmap: OlMap, pixel) => {
    let listFeatures: FeatureLike[] = [];

    listFeatures = olmap.getFeaturesAtPixel(pixel) ?? [];

    let feature = listFeatures[0];
    if (feature) {
      tooltip.style.left = pixel[0] + 'px';
      tooltip.style.top = pixel[1] + 'px';
      if (feature !== currentFeature) {
        const tGeometry: IGeometry = feature.getGeometry()?.get('tGeometry');

        if (tGeometry) {
          tooltip.style.visibility = 'visible';
          tooltip.innerText = tGeometry.name ?? '';
        } else {
          tooltip.style.visibility = 'hidden';
        }
      }
    } else {
      tooltip.style.visibility = 'hidden';
    }
    currentFeature = feature;
  };

  const pointerMoveInMap = olmap => evt => {
    const { pixel } = evt;

    if (evt.dragging) {
      tooltip.style.visibility = 'hidden';
      currentFeature = undefined;
      return;
    }

    displayFeatureTooltip(olmap, pixel);
  };
  //#endregion

  //const throttledAdjustZoomIndoorLayer = _.throttle(adjustZoomIndoorLayer, 1000);
  const throttledAdjustZoomIndoorLayer = useCallback(_.throttle(adjustZoomIndoorLayer, 1000), [
    state.isViewSatellite,
    stateMap.imageLayer,
  ]);
  //#endregion

  //#region Settings
  const putSettingsUserTileDefaultView = (tile: 'satellite' | 'normal') => {
    const { parameterId, defaultViewId } = settings?.defaultView || {};

    const handleUpdateSettings = v => {
      if (badge) {
        getSettings(dispatch, badge);
        dispatch(requestActions.cleanKey('defaultView'));
      }
    };

    putUserDefaultView(dispatch, parameterId, defaultViewId, tile).then(handleUpdateSettings);
  };
  //#endregion
  //#endregion

  //#region Renders
  const renderLoading = () => {
    return (
      isLoading() && (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          className="top-1/2 left-1/2 w-full h-full z-[3] absolute -translate-1/2 text-center bg-[#80808024] text-[rgb(42,92,167)]"
        >
          <Loading type="circular" />
        </Grid>
      )
    );
  };

  const renderNotFound = () => {
    updateMap();
    if (!tMapElement) return;
    if (isLoading()) return;
    if (someItemWithLocation()) return;

    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        className="top-1/2 left-1/2 w-full h-full z-[3] absolute -translate-1/2 text-center bg-[80808024]"
        sx={{
          color: state.isViewSatellite ? 'white' : theme.palette.primary.main,
        }}
      >
        <Grid mr={1}>
          <Icon icon="gps_off" size={32} />
        </Grid>
        <Grid>
          <Typography variant="h4" color={state.isViewSatellite ? 'white' : 'primary'} text="Unknown location" />
        </Grid>
      </Grid>
    );
  };
  //#endregion

  //#region Effects
  const effectFetchView = (deps: React.DependencyList) => {
    useEffect(() => {
      if (!stateMap.view) !props.view && fetchDefaultView();

      return () => {
        if (defaultViewFetch) dispatch(requestActions.cleanKey('mapView'));
      };
    }, deps);
  };

  const effectGenerateOpenLayerMap = (deps: React.DependencyList) => {
    useEffect(() => {
      if (!olmap && stateMap.view) {
        log.info('GenerateOpenLayersMap');
        generateOpenLayersMap(stateMap.view);
      }
      return () => {
        setStateMap({ imageLayer: null });
        olmap?.setTarget(undefined);
        setOlMap(null);
      };
    }, deps);
  };

  const effectGenerateImageLayer = (deps: React.DependencyList) => {
    //When openlayers map is ready we create imageLayer if exist
    //When is drawer we create vectorSource and layer
    useEffect(() => {
      if (olmap) {
        if (!stateMap.imageLayer) {
          log.info('GenerateImageLayer');
          generateImageLayer();
        } else {
        }
      }
    }, deps);
  };

  const effectGeneratePointerMoveZoom = (deps: React.DependencyList) => {
    useEffect(() => {
      adjustZoomIndoorLayerEvent();

      return () => {
        olmap?.un('pointermove', throttledAdjustZoomIndoorLayer);
      };
    }, deps);
  };

  const effectCreateMapElement = (deps: React.DependencyList) => {
    // When readings changes update listItems

    useEffectAfterMount(() => {
      if (tElement) {
        const getPositions = async () => {
          if (stateMap.view) {
            log.info('TransformElement', tElement);
            const itemMap = updateMapElement(tElement, unitFormatter);
            itemMap.lastPosition = tElement.getLastPosition(stateMap.view.getReadingTypePosition());
            //const newRecord: Record<TElementID, CMapElement> = {};
            //newRecord[tElement.getID()] = itemMap;
            //await getBatchElementPositions(dispatch, [tElement.getID()], newRecord, stateMap.view, false);
            log.info('CreateCMapElement', itemMap);
            setCMapElement(itemMap);
          }
        };
        getPositions();
      }
    }, deps);
  };

  const effectPrintMapElement = (deps: React.DependencyList) => {
    // When update listItems, generate vectorSource and vectorLayer, center position

    useEffectAfterMount(() => {
      if (tMapElement && stateMap.view) {
        log.info('Generate vectorSource and vectorLayer');
        const item = tMapElement;
        generateVectorSource({ logicalId: item.getID() });
        generateVectorLayer({ logicalId: item.getID() });

        if (item.lastPosition) {
          const coordinates = getCoordinatesFromItem(item);

          if (coordinates) {
            const icon = item.getIcon();
            generateIcon(item.getID(), coordinates, icon);
          }
        }

        //setTimeout(() => {
        onCenterView();
        //}, 800);
      }
    }, deps);
  };

  const effectComponentUnmount = () => {
    useEffect(() => {
      return () => {
        olmap?.setTarget(undefined);
      };
    }, []);
  };
  //#endregion

  //Map
  effectFetchView([stateMap.selectedViewId, stateMap.view, defaultViewFetch, defaultViewError]);
  effectGenerateOpenLayerMap([stateMap.view]);
  effectCreateMapElement([tElement, stateMap.view]);
  effectPrintMapElement([tMapElement]);
  effectGenerateImageLayer([olmap, stateMap.view, stateMap.imageLayer]);
  effectGeneratePointerMoveZoom([state.isViewSatellite, stateMap.imageLayer]);

  /*   useEffectAfterMount(() => {
    if (props.element && props.element.getID() !== tElement?.getID()) {
      setTElement(props.element);
    }
  }, [props.element]); */

  effectComponentUnmount();

  //#region Wrappers
  useEffect(() => {
    if (olmap && ref && ref.current && ref.current.notifyParentAboutChange) {
      ref.current.notifyParentAboutChange();
    }
  }, [olmap]);

  useImperativeHandle(ref, () => ({
    notifyParentAboutChange() {
      if (props.onOlmapChange && olmap && stateMap.view) {
        props.onOlmapChange(olmap, stateMap.view);
      }
    },
    onCenterView() {
      //onCenterView();
    },
    getOlmap() {
      return olmap;
    },
  }));
  //#endregion

  const classes = useStyles({ ...state, theme });
  return (
    <div id="quickmap" className={classes.quickmap}>
      {renderLoading()}
      {renderNotFound()}
      <div id="map" ref={mapRef} className={classes.map}>
        <div
          id="tooltip"
          className="absolute inline-block h-auto w-auto z-[100] bg-[#333] text-white text-center rounded p-[5px] left-1/2 translate-x-[3%] invisible pointer-events-none"
        />
        <div className="flex absolute top-2 left-10 z-[2]">
          {stateMap.view?.isWorldView() && (
            <IconButton className={classes.iconBtnMap} onClick={onChangeTileView}>
              <Icon icon="maps-layers" size={20} className="text-[24px] opacity-80" />
            </IconButton>
          )}
          {
            <IconButton className={classes.iconBtnMap} onClick={onCenterView}>
              <Icon icon="my_location" size={20} className="text-[24px] opacity-80" />
            </IconButton>
          }
        </div>
        <div className="flex absolute top-2 right-0.5">
          <Tooltip title={!state.hideOtherGeometries ? 'Hide other geometries' : 'Show other geometries'}>
            <IconButton sx={{ zIndex: 2 }} className={classes.iconBtnMap} onClick={onClickHideOtherGeometries}>
              <Icon
                icon={!state.hideOtherGeometries ? 'visibilityOff' : 'visibility'}
                size={20}
                className="text-[24px] opacity-80"
              />
            </IconButton>
          </Tooltip>
          <WrapperEntitySelector
            showAsButton={onClick => (
              <Tooltip title="Change default view">
                <IconButton sx={{ zIndex: 5 }} className={classes.iconBtnMap} onClick={onClick}>
                  <Icon icon="settings" size={20} className="text-[24px] opacity-80" />
                </IconButton>
              </Tooltip>
            )}
            searchComponent={generateSearchEntitySelector('view')}
            value={stateMap.view}
            onChangeEntity={v => onChangeView(v as CView)}
            label="View to draw the geometry"
          />
        </div>
      </div>
      {!props.hideInfoSideBar && someItemWithLocation() && tMapElement && stateMap.view && (
        <InfoSidebar
          view={stateMap.view}
          elementsMapSelected={[tMapElement]}
          elementSelected={tMapElement}
          isQuickAccess={props.isQuickAccess}
        />
      )}
    </div>
  );
});

export default QuickMap;
